import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { LocateFixed, MapPinned } from 'lucide-react';
import { MAX_RADIUS_MILES, serviceAreaLabel, type Coverage, type PostalPoint, type ZipBoundary, type LeadType } from '../shared/contracts';
import { distanceMiles, evaluateCoverage, METERS_PER_MILE } from '../shared/routing';
import { useNearbyPostal, useZipBoundaries } from './useZipBoundaries';

export function AreaMap({ coverage, postal, selected, onSelect, leadType, nearby, zipSummaries, focusZip }: { coverage: Coverage; postal: PostalPoint[]; selected: string | null; onSelect: (id: string) => void; leadType: LeadType; nearby: ReturnType<typeof useNearbyPostal>; zipSummaries?: Record<string,string>; focusZip?: { zip: string; request: number } | null }) {
  const element = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const overlays = useRef<L.LayerGroup | null>(null);
  const lastFrame = useRef('');
  const manualView = useRef(false);
  const enteredZips = useRef(new Set<string>());
  const lastFocus = useRef<typeof focusZip>(null);
  const [tileError, setTileError] = useState(false);
  const [showBoundaries, setShowBoundaries] = useState(true);
  const [showLabels, setShowLabels] = useState(false);
  const [alwaysShowBorders, setAlwaysShowBorders] = useState(false);
  const zips = [...new Set(coverage.rules.flatMap(r => r.shape === 'radius' ? [r.centerZip ?? ''] : r.zips))].filter(zip => /^\d{5}$/.test(zip)).sort();
  const mapPostal = [...new Map([...postal,...nearby.points].map(point => [point.zip,point])).values()];
  const centers = mapPostal.filter(point => zips.includes(point.zip));
  // Prefetch the full radius range once. Slider movement only recolors local polygons.
  const candidateZips = [...new Set([...zips, ...mapPostal.filter(point => centers.some(center => distanceMiles(center,point) <= MAX_RADIUS_MILES)).map(point => point.zip)])].sort();
  const zipKey = (zipSummaries ? zips : candidateZips.slice(0, 500)).join(',');
  const boundaryData = useZipBoundaries(zipKey, showBoundaries);
  const boundaries = showBoundaries ? boundaryData.boundaries : {};
  const selectedRule = coverage.rules.find(r => r.id === selected);
  useEffect(() => {
    if (!element.current) return;
    const m = L.map(element.current, { zoomControl: false, scrollWheelZoom: !!zipSummaries }).setView([28.005, -82.70], 10);
    m.createPane('zipBoundaries').style.zIndex = '350';
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', { referrerPolicy: 'strict-origin', attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>', maxZoom: 18 }).on('tileerror', () => setTileError(true)).addTo(m);
    const zoomControl = L.control.zoom({ position: 'bottomright' }).addTo(m);
    const zoomLevel = document.createElement('output');
    zoomLevel.className = 'map-zoom-level';
    zoomLevel.setAttribute('aria-label', 'Map zoom level');
    const updateZoomLevel = () => {
      zoomLevel.textContent = `Zoom ${m.getZoom()}`;
      m.getContainer().classList.toggle('map-zip-borders-hidden', m.getZoom() <= 8);
    };
    updateZoomLevel();
    zoomControl.getContainer()!.appendChild(zoomLevel);
    m.on('zoomend', updateZoomLevel);
    m.attributionControl.addAttribution('<a href="https://www.census.gov/programs-surveys/geography/guidance/geo-areas/zctas.html">U.S. Census ZIP areas (ZCTA)</a>');
    overlays.current = L.layerGroup().addTo(m); map.current = m;
    // User navigation owns the viewport until this map is remounted. Data
    // refreshes and arriving ZIP boundaries must not undo a pan or zoom.
    const container = element.current;
    const preserveView = () => { manualView.current = true; };
    for (const event of ['pointerdown', 'wheel', 'keydown']) container.addEventListener(event, preserveView, { capture: true, passive: true });
    const resize = new ResizeObserver(() => m.invalidateSize({ pan: false })); resize.observe(container);
    return () => {
      for (const event of ['pointerdown', 'wheel', 'keydown']) container.removeEventListener(event, preserveView, true);
      resize.disconnect(); m.off('zoomend', updateZoomLevel); m.remove(); map.current = null; lastFrame.current = ''; manualView.current = false;
    };
  }, []);
  useEffect(() => {
    const current = new Set(zips);
    // A newly completed ZIP entry explicitly requests a fresh collective frame.
    // Radius edits, selection, and data refreshes keep the user's viewport.
    if (!zipSummaries && zips.some(zip => !enteredZips.current.has(zip))) {
      manualView.current = false;
      lastFrame.current = '';
    }
    enteredZips.current = current;
  }, [zips.join(','), !!zipSummaries]);
  useEffect(() => {
    const layers = overlays.current;
    if (!layers) return;
    layers.clearLayers();
    for (const zip of zipKey ? zipKey.split(',') : []) {
      const boundary = boundaries[zip]; if (!boundary) continue;
      const match = evaluateCoverage(coverage, { zip, point: mapPostal.find(point => point.zip === zip) ?? null, leadType });
      const rule = coverage.rules.find(rule => rule.id === match.ruleId);
      const highlighted = ['preferred','backup','excluded'].includes(match.tier);
      const color = match.tier === 'excluded' ? '#c46350' : match.tier === 'backup' ? '#c77b08' : match.tier === 'preferred' ? '#16816d' : '#81949e';
      const label = document.createElement('span'); label.textContent = zip;
      if (!showLabels) {
        const detail = document.createElement('span');
        detail.textContent = rule
          ? `${match.tier === 'excluded' ? 'Excluded by' : match.tier === 'backup' ? 'Backup via' : 'Included via'} ${serviceAreaLabel(rule)}`
          : match.reason;
        label.textContent = `${zip}: `;
        label.appendChild(detail);
        if (rule) {
          const hint = document.createElement('span'); hint.style.display = 'block'; hint.style.fontWeight = '400';
          hint.textContent = 'Click ZIP to edit this service area'; label.appendChild(hint);
        }
      }
      if (zipSummaries) label.textContent = showLabels ? `${zip} · ${zipSummaries[zip]?.split(' ')[0] ?? ''}` : `${zip}: ${zipSummaries[zip] ?? ''} · Click to see agents`;
      L.geoJSON(boundary, { pane: 'zipBoundaries', interactive: highlighted, style: { color, weight: zipSummaries && selected === zip ? 4 : highlighted ? 2 : 1,
        fillColor: match.tier === 'backup' ? '#f59e0b' : color, fillOpacity: match.tier === 'backup' ? 0.48 : highlighted ? 0.22 : 0, dashArray: highlighted ? undefined : '3 5' },
        onEachFeature: (_feature, layer) => {
          if (!highlighted) return;
          layer.bindTooltip(label, { permanent: showLabels && highlighted, sticky: !showLabels, direction: showLabels ? 'center' : 'auto', className: 'zip-boundary-label' });
          layer.on('click', () => { if (rule) onSelect(rule.id); });
        },
      }).addTo(layers);
    }
    const order = { backup: 0, preferred: 1, exclude: 2 };
    for (const rule of (zipSummaries ? [] : [...coverage.rules]).sort((a, b) => order[a.effect] - order[b.effect])) {
      const inactive = !rule.enabled || (rule.effect === 'backup' && !coverage.backupEnabled);
      const color = rule.effect === 'preferred' ? '#16816d' : rule.effect === 'backup' ? '#c77b08' : '#c46350';
      if (rule.shape === 'radius' && rule.center && rule.radiusMiles) {
        const circle = L.circle([rule.center.lat, rule.center.lng], { radius: rule.radiusMiles * METERS_PER_MILE, color: rule.effect === 'preferred' ? '#116653' : color, weight: selected === rule.id ? 2.5 : 1.5,
          fill: false, dashArray: '6 7', opacity: inactive ? 0.3 : rule.effect === 'preferred' ? 0.9 : 0.6 }).addTo(layers);
        const label = document.createElement('div'); label.textContent = `${serviceAreaLabel(rule)}`;
        circle.bindTooltip(label).on('click', () => onSelect(rule.id));
        L.circleMarker([rule.center.lat, rule.center.lng], { radius: 5, color: '#fff', weight: 2, fillColor: color, fillOpacity: 1 }).addTo(layers);
      } else for (const zip of rule.shape === 'radius' && rule.radiusMiles === 0 ? [rule.centerZip!] : rule.zips) {
        const location = mapPostal.find(p => p.zip === zip); if (!location) continue;
        const pin = L.circleMarker([location.lat, location.lng], { radius: rule.effect === 'exclude' ? 9 : 7, color, weight: 2, fillColor: '#fff', fillOpacity: 1 }).addTo(layers);
        const label = document.createElement('div'); label.textContent = `${zip} · ${rule.effect === 'exclude' ? 'entire ZIP excluded' : 'ZIP code only'}`;
        pin.bindTooltip(label).on('click', () => onSelect(rule.id));
        if (rule.effect === 'exclude') L.marker([location.lat, location.lng], { interactive: false, icon: L.divIcon({ className: 'exclude-marker', html: '×', iconSize: [18, 18], iconAnchor: [9, 9] }) }).addTo(layers);
      }
    }
    const { bounds, key } = coverageBounds(coverage, mapPostal, boundaries, leadType);
    if (bounds.isValid() && map.current) {
      // Frame all entered ZIPs together; keep the scale steady while circles shrink.
      if (!manualView.current && (lastFrame.current !== key || !map.current.getBounds().contains(bounds))) {
        map.current.fitBounds(bounds, { padding: [55,55], maxZoom: 12, animate: false });
      }
      lastFrame.current = key;
    } else lastFrame.current = '';
    // Redraws and overlapping SVG layers can leave a tooltip open without a
    // matching Leaflet mouseout. Check the actual pointer target as well.
    const hovered = new Set<L.Path>();
    const track = (layer: L.Layer) => {
      if (layer instanceof L.LayerGroup) layer.eachLayer(track);
      if (layer instanceof L.Path && layer.getTooltip() && !layer.getTooltip()!.options.permanent) {
        layer.on('tooltipopen', () => hovered.add(layer));
        layer.on('tooltipclose', () => hovered.delete(layer));
      }
    };
    layers.eachLayer(track);
    const dismissOutside = (event: PointerEvent) => {
      for (const layer of hovered) {
        if (!(event.target instanceof Node) || !layer.getElement()?.contains(event.target)) layer.closeTooltip();
      }
    };
    const dismissAll = () => { for (const layer of hovered) layer.closeTooltip(); };
    const container = map.current!.getContainer();
    container.addEventListener('pointermove', dismissOutside);
    container.addEventListener('pointerleave', dismissAll);
    return () => {
      container.removeEventListener('pointermove', dismissOutside);
      container.removeEventListener('pointerleave', dismissAll);
      dismissAll();
      layers.clearLayers();
    };
  }, [coverage, postal, nearby.points, selected, onSelect, boundaryData.boundaries, showBoundaries, showLabels, zipKey, leadType, zipSummaries]);
  useEffect(() => {
    if (!focusZip || lastFocus.current === focusZip || !map.current) return;
    const boundary = boundaries[focusZip.zip];
    const bounds = boundary ? L.geoJSON(boundary).getBounds() : null;
    const point = mapPostal.find(point => point.zip === focusZip.zip);
    if (!bounds?.isValid() && !point) return;
    // Consume each explicit list click once, including repeat clicks on the same
    // ZIP. Later boundary arrivals and refreshes must not reset user navigation.
    lastFocus.current = focusZip;
    manualView.current = true;
    if (bounds?.isValid()) map.current.fitBounds(bounds, { padding: [45,45], maxZoom: 14 });
    else if (point) map.current.setView([point.lat, point.lng], 12);
  }, [focusZip, boundaryData.boundaries, showBoundaries, postal, nearby.points]);
  const recenter = () => {
    manualView.current = true;
    const { bounds } = coverageBounds(coverage, mapPostal, boundaries, leadType);
    if (bounds.isValid()) map.current?.fitBounds(bounds, { padding: [32,32], maxZoom: 12 });
  };
  return <div className={`map-shell${alwaysShowBorders ? ' map-always-show-borders' : ''}`}>
    <div ref={element} className="map" aria-label="Interactive service-area map" />
    <div className="map-title"><MapPinned size={16} /><span>{zipSummaries ? 'Team' : 'Matching'} {leadType} ZIPs</span><span className="map-live">{zipSummaries ? 'Saved' : 'Draft'}</span></div>
    <button className={`map-recenter ${zipSummaries ? 'button secondary map-center-button' : 'icon-button'}`} onClick={recenter} title="Fit all service areas" aria-label={zipSummaries ? 'Center map' : 'Fit all service areas'}><LocateFixed size={18} />{zipSummaries && 'Center map'}</button>
    <div className="map-boundary-toggle">
      <label><input type="checkbox" checked={showBoundaries} onChange={e => setShowBoundaries(e.target.checked)}/> ZIP boundaries</label>
      <label><input type="checkbox" checked={alwaysShowBorders} disabled={!showBoundaries} onChange={e => setAlwaysShowBorders(e.target.checked)}/> Always show borders</label>
      <label><input type="checkbox" checked={showLabels} disabled={!showBoundaries} onChange={e => setShowLabels(e.target.checked)}/> ZIP labels</label>
    </div>
    {selectedRule && !zipSummaries && <div className="map-radius-readout"><strong>{serviceAreaLabel(selectedRule)}</strong><span>{selectedRule.shape === 'radius' && selectedRule.radiusMiles ? `${selectedRule.radiusMiles} miles` : 'ZIP code only'}{!selectedRule.enabled ? ' · area disabled' : selectedRule.effect === 'backup' && !coverage.backupEnabled ? ' · backup disabled' : ''}</span></div>}
    <div className="map-legend"><span><i className="legend-preferred" />Preferred</span><span><i className="legend-backup" />Backup</span><span><i className="legend-excluded" />Excluded</span></div>
    {tileError && <div className="map-offline">Some map tiles could not load. Area rules and ZIP testing still work.</div>}
    {showBoundaries && (nearby.loading || nearby.errors.length > 0 || boundaryData.loading || boundaryData.errors.length > 0 || boundaryData.missing.length > 0 || (!zipSummaries && candidateZips.length > 500)) && <div className="boundary-status" role="status">
      {nearby.loading || boundaryData.loading ? 'Loading matching ZIP areas…' : nearby.errors.length || boundaryData.errors.length ? <>Some ZIP areas could not load. <button onClick={() => { nearby.retry(); boundaryData.retry(); }}>Retry</button></> : boundaryData.missing.length ? `No Census boundary for ${boundaryData.missing.slice(0,3).join(', ')}${boundaryData.missing.length > 3 ? '…' : ''}.` : 'Showing boundaries for the first 500 ZIPs.'}
    </div>}
  </div>;
}

function coverageBounds(coverage: Coverage, postal: PostalPoint[], boundaries: Record<string, ZipBoundary | null>, leadType: LeadType) {
  const bounds = L.latLngBounds([]); const locations: string[] = [];
  for (const rule of coverage.rules) {
    for (const zip of rule.shape === 'radius' ? [rule.centerZip ?? ''] : rule.zips) {
      const boundary = boundaries[zip]; if (!boundary) continue;
      const area = L.geoJSON(boundary).getBounds();
      if (area.isValid()) { bounds.extend(area); locations.push(`${zip}:${area.toBBoxString()}`); }
    }
    const points = rule.shape === 'radius' ? (rule.center ? [rule.center] : []) : postal.filter(p => rule.zips.includes(p.zip));
    for (const point of points) {
      locations.push(`${rule.id}:${point.lat}:${point.lng}`);
      bounds.extend(L.latLng(point.lat,point.lng).toBounds(2 * Math.max(rule.shape === 'radius' ? rule.radiusMiles ?? 0 : 0, 0.5) * METERS_PER_MILE));
    }
  }
  for (const [zip,boundary] of Object.entries(boundaries)) {
    if (!boundary) continue;
    const match = evaluateCoverage(coverage, { zip, point: postal.find(p => p.zip === zip) ?? null, leadType });
    if (['preferred','backup','excluded'].includes(match.tier)) bounds.extend(L.geoJSON(boundary).getBounds());
  }
  return { bounds, key: locations.sort().join('|') };
}
